import React, { useEffect, useState } from 'react';
import {
  ArrowBack,
  Check,
  Info,
  Warning,
  FormatListBulleted,
  Receipt,
  Download,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { useReturn } from '../../context/ReturnContext';
import { useOrder } from '../../context/OrderContext';
import { Return as ReturnModel } from '../../models/Return';
import { ReturnStatus, translateReturnStatus } from '../../models/Return';
import { ReturnReason, translateReturnReason } from '../../models/ReturnItem';
import Loading from '../../components/global/loading/Loading';
import Confirm from '../../components/modals/confirmModal/Confirm';
import { useAuth } from '../../context/AuthContext';
import translateOrderStatus from '../../helperFunctions/translateOrderStatus';
import { useConfig } from '../../utils/useConfig';
import Seo from '../../components/seo/Seo';

function Return() {
  const { id } = useParams();
  const config = useConfig();
  const { getReturn, changeReturnStatus, processRefund } = useReturn();
  const { getOrder } = useOrder();
  const { isAuthenticated, user } = useAuth();
  const [returnData, setReturnData] = useState<ReturnModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const isOnAdmin = location.pathname.includes('admin');
  const isAdmin = user?.role === 'Admin';

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  useEffect(() => {
    if (isAuthenticated && location.pathname.startsWith(encodeURI('/zwrot'))) {
      navigate(`/profil/zwroty/${id}`);
    }
  }, [id, isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    if (!id) {
      toast.error('Nie znaleziono zwrotu');
      navigate('/');
      return;
    }

    const fetchReturn = async () => {
      setIsLoading(true);
      try {
        const fetchedReturn = await getReturn(id);
        setReturnData(fetchedReturn);

        // Jeśli zwrot ma powiązane zamówienie, pobierz również dane zamówienia
        if (!fetchedReturn.order && fetchedReturn.orderId) {
          try {
            const order = await getOrder(fetchedReturn.orderId);
            setReturnData((prev) => (prev ? { ...prev, order } : prev));
          } catch (orderError: any) {
            console.error('Błąd podczas pobierania zamówienia:', orderError);
          }
        }

        setError(null);
      } catch (error: any) {
        console.error('Błąd podczas pobierania zwrotu:', error);
        setError(error.message || 'Wystąpił błąd podczas pobierania zwrotu');
        toast.error(error.message || 'Wystąpił błąd podczas pobierania zwrotu');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReturn();
  }, [id, getReturn, getOrder, navigate]);

  const handleBackClick = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 1) {
      const newPath = '/' + pathParts.slice(0, -1).join('/');
      navigate(newPath);
    } else {
      navigate('/');
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    const toastId = toast.loading('Zatwierdzanie zwrotu...');
    try {
      await changeReturnStatus(id, ReturnStatus.APPROVED);
      toast.update(toastId, {
        render: 'Zwrot został zatwierdzony',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      // Odśwież dane zwrotu
      const updatedReturn = await getReturn(id);
      setReturnData(updatedReturn);
    } catch (error: any) {
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas zatwierdzania zwrotu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleReject = async () => {
    if (!id) return;

    const toastId = toast.loading('Odrzucanie zwrotu...');
    try {
      await changeReturnStatus(id, ReturnStatus.REJECTED);
      toast.update(toastId, {
        render: 'Zwrot został odrzucony',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      // Odśwież dane zwrotu
      const updatedReturn = await getReturn(id);
      setReturnData(updatedReturn);
    } catch (error: any) {
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas odrzucania zwrotu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleComplete = async () => {
    if (!id) return;

    const toastId = toast.loading('Finalizowanie zwrotu...');
    try {
      await changeReturnStatus(id, ReturnStatus.COMPLETED);
      toast.update(toastId, {
        render: 'Zwrot został zakończony',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      // Odśwież dane zwrotu
      const updatedReturn = await getReturn(id);
      setReturnData(updatedReturn);
    } catch (error: any) {
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas finalizowania zwrotu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleRefund = async () => {
    if (!id) return;

    const toastId = toast.loading('Przetwarzanie zwrotu pieniędzy...');
    try {
      await processRefund(id);
      toast.update(toastId, {
        render: 'Zwrot pieniędzy został przetworzony pomyślnie',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      // Odśwież dane zwrotu
      const updatedReturn = await getReturn(id);
      setReturnData(updatedReturn);
    } catch (error: any) {
      toast.update(toastId, {
        render:
          error.message ||
          'Wystąpił błąd podczas przetwarzania zwrotu pieniędzy',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const getReturnStatusColor = (status: ReturnStatus) => {
    switch (status) {
      case ReturnStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-200';
      case ReturnStatus.APPROVED:
        return `bg-blue-50 text-blue-700 border-blue-200`;
      case ReturnStatus.REJECTED:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  // Obliczenie łącznej kwoty zwrotu
  const calculateTotalRefund = () => {
    if (!returnData?.returnItems || !returnData?.order?.items) return 0;

    let totalRefund = 0;

    returnData.returnItems.forEach((returnItem) => {
      const orderItem = returnData.order?.items?.find(
        (oi) => oi.id === returnItem.orderItemId,
      );

      if (orderItem) {
        totalRefund += orderItem.productPrice * returnItem.quantity;
      }
    });

    return totalRefund.toFixed(2);
  };

  // Generuj PDF zwrotu
  const generateReturnPdf = async () => {
    if (!returnData) return;

    setIsPdfGenerating(true);
    const toastId = toast.loading('Generowanie dokumentu zwrotu...');

    try {
      // Tworzenie PDF z polskimi znakami
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Dodajemy standardową czcionkę
      pdf.setFont('helvetica', 'normal');

      // Funkcja pomocnicza do dodawania polskich znaków - używamy bezpośrednio tekstu zamiast encode/unescape
      const addText = (text: string, x: number, y: number, options?: any) => {
        pdf.text(text, x, y, options);
      };

      const pageWidth = pdf.internal.pageSize.getWidth();

      // Nagłówek
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      addText('Dokument zwrotu', pageWidth / 2, 15, { align: 'center' });

      // Logo firmy - kwadratowe
      try {
        const img = new Image();
        img.src = config.company.logo;
        // Kwadratowe logo - równa szerokość i wysokość (30x30mm)
        pdf.addImage(img, 'PNG', pageWidth / 2 - 15, 20, 30, 30);
      } catch (logoError) {
        console.error('Nie udało się załadować logo:', logoError);
      }

      // Informacje o zwrocie
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      addText(`Numer zwrotu: ${returnData.returnNumber}`, 15, 55);
      addText(
        `Data utworzenia: ${new Date(returnData.createdAt).toLocaleDateString('pl-PL')}`,
        15,
        62,
      );
      addText(
        `Numer zamówienia: ${returnData.order?.orderNumber || returnData.orderId}`,
        15,
        69,
      );

      // Linia oddzielająca
      pdf.line(15, 75, pageWidth - 15, 75);

      // Adres
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      addText('Adres do wysyłki zwrotu:', 15, 85);
      pdf.setFont('helvetica', 'normal');
      addText(config.company.name, 15, 92);
      addText(config.contact.address.street, 15, 99);
      addText(
        `${config.contact.address.postal} ${config.contact.address.city}`,
        15,
        106,
      );

      // Informacje o kliencie
      pdf.setFont('helvetica', 'bold');
      addText('Dane klienta:', 15, 120);
      pdf.setFont('helvetica', 'normal');
      addText(`${returnData.order?.fullName || ''}`, 15, 127);
      addText(`E-mail: ${returnData.order?.email || ''}`, 15, 134);
      addText(`Telefon: ${returnData.order?.phone || ''}`, 15, 141);

      // Lista produktów
      pdf.setFont('helvetica', 'bold');
      addText('Zwracane przedmioty:', 15, 155);
      pdf.setFont('helvetica', 'normal');

      let yPosition = 162;
      returnData.returnItems?.forEach((item, index) => {
        const orderItem = returnData.order?.items?.find(
          (oi) => oi.id === item.orderItemId,
        );

        // Nazwa produktu
        addText(
          `${index + 1}. ${orderItem?.product?.name || 'Produkt'}`,
          20,
          yPosition,
        );

        // Ilość
        addText(`Ilość: ${item.quantity}`, 30, yPosition + 7);

        // Powód zwrotu
        addText(
          `Powód: ${translateReturnReason(item.reason as ReturnReason)}`,
          30,
          yPosition + 14,
        );

        yPosition += 25;

        // Sprawdź czy trzeba dodać nową stronę
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
      });

      // Instrukcje
      pdf.setFont('helvetica', 'bold');
      addText('Instrukcje:', 15, yPosition);
      pdf.setFont('helvetica', 'normal');
      addText('1. Wydrukuj ten dokument.', 15, yPosition + 7);
      addText(
        '2. Dołącz go do paczki ze zwracanymi produktami.',
        15,
        yPosition + 14,
      );
      addText('3. Wyślij paczkę na powyższy adres.', 15, yPosition + 21);
      addText('4. Zachowaj potwierdzenie wysyłki.', 15, yPosition + 28);

      // Informacja pomocnicza
      pdf.setFontSize(10);
      addText(
        'Dołączenie tego dokumentu nie jest obowiązkowe, ale znacznie przyspieszy proces zwrotu.',
        15,
        yPosition + 40,
      );

      // Stopka
      const date = new Date();
      pdf.setFontSize(8);
      addText(
        `Dokument wygenerowano: ${date.toLocaleDateString('pl-PL')} ${date.toLocaleTimeString('pl-PL')}`,
        15,
        pdf.internal.pageSize.getHeight() - 10,
      );
      addText(
        `${config.company.name} - ${config.contact.phone}`,
        pageWidth - 15,
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'right' },
      );

      // Zapisz PDF
      pdf.save(`zwrot_${returnData.returnNumber}.pdf`);

      toast.update(toastId, {
        render: 'Dokument został wygenerowany pomyślnie',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Błąd podczas generowania PDF:', error);
      toast.update(toastId, {
        render: 'Wystąpił błąd podczas generowania dokumentu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsPdfGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (error || !returnData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-medium text-red-800 mb-2">
            Wystąpił błąd podczas ładowania danych zwrotu
          </h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Powrót do strony głównej
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Przycisk powrotu */}
        {(location.pathname.includes('admin') ||
          location.pathname.includes('profil')) && (
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowBack fontSize="small" />
            <span className="font-medium">Powrót</span>
          </motion.button>
        )}

        {/* Panel administracyjny */}
        {isOnAdmin && isAdmin && (
          <motion.div
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8"
            variants={itemVariants}
          >
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h2 className="text-lg font-medium text-gray-800">
                Panel administracyjny
              </h2>
            </div>

            <div className="p-5 space-y-6">
              <div>
                <h3 className="font-medium text-gray-800 mb-2">
                  Akcje administracyjne
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  Poniżej znajdziesz przyciski do zarządzania zwrotem oraz
                  informacje krok po kroku, które pomogą Ci w obsłudze zwrotu.
                </p>

                <div className="flex flex-wrap gap-3 mb-4">
                  {returnData.status === ReturnStatus.PENDING && (
                    <>
                      <motion.button
                        onClick={() => setShowApproveModal(true)}
                        className="px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Check fontSize="small" />
                        Zatwierdź zwrot
                      </motion.button>

                      <motion.button
                        onClick={() => setShowRejectModal(true)}
                        className="px-4 py-2 bg-red-50 border border-red-300 text-red-700 hover:bg-red-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Warning fontSize="small" />
                        Odrzuć zwrot
                      </motion.button>
                    </>
                  )}

                  {returnData.status === ReturnStatus.APPROVED && (
                    <>
                      <motion.button
                        onClick={() => setShowCompleteModal(true)}
                        className="px-4 py-2 bg-green-50 border border-green-300 text-green-700 hover:bg-green-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Check fontSize="small" />
                        Zakończ zwrot
                      </motion.button>

                      <motion.button
                        onClick={() => setShowRefundModal(true)}
                        className="px-4 py-2 bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Receipt fontSize="small" />
                        Wykonaj zwrot pieniędzy
                      </motion.button>
                    </>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">
                  Profil klienta
                </h3>
                {returnData.order?.userId ? (
                  <Link
                    to={`/admin/użytkownicy/${returnData.order.userId}`}
                    className="text-primary hover:text-primary-dark underline transition-colors"
                  >
                    Zobacz profil klienta
                  </Link>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Klient nie jest zarejestrowany
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Nagłówek zwrotu */}
        <motion.div className="mb-8" variants={itemVariants}>
          <h1 className="text-2xl font-medium text-gray-900 mb-2">
            Zwrot:{' '}
            <span className="font-bold text-primary">
              {returnData.returnNumber}
            </span>
          </h1>

          <div className="flex flex-wrap gap-3 mt-4">
            <div
              className={`px-3 py-1.5 rounded-full ${getReturnStatusColor(returnData.status)} flex items-center gap-1 text-sm font-medium`}
            >
              <Check fontSize="small" />
              Status zwrotu: {translateReturnStatus(returnData.status)}
            </div>

            <div className="px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 flex items-center gap-1 text-sm font-medium">
              <FormatListBulleted fontSize="small" />
              Zamówienie:{' '}
              <Link
                to={`/zamówienie/${returnData.orderId}`}
                className="text-primary hover:underline"
              >
                {returnData.order?.orderNumber || returnData.orderId}
              </Link>
            </div>

            {returnData.order?.orderStatus && (
              <div className="px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 flex items-center gap-1 text-sm font-medium">
                <Info fontSize="small" />
                Status zamówienia:{' '}
                {translateOrderStatus(returnData.order.orderStatus)}
              </div>
            )}
          </div>

          <div className="mt-3 text-gray-600">
            Data złożenia:{' '}
            <span className="font-medium">
              {new Date(returnData.createdAt).toLocaleDateString('pl-PL')}
            </span>{' '}
            o{' '}
            <span className="font-medium">
              {new Date(returnData.createdAt).toLocaleTimeString('pl-PL', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </motion.div>

        {/* Główna sekcja zwrotu */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Lewa kolumna - informacje o zwrocie */}
          <motion.div variants={itemVariants}>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h3 className="text-base font-medium text-gray-800 mb-3">
                  Szczegóły zwrotu
                </h3>

                {returnData.comments && (
                  <div className="bg-gray-50 p-3 mb-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      Komentarz klienta:
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {returnData.comments}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  {returnData.returnItems &&
                  returnData.returnItems.length > 0 ? (
                    returnData.returnItems.map((item, index) => {
                      const orderItem = returnData.order?.items?.find(
                        (oi) => oi.id === item.orderItemId,
                      );

                      return (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-md p-3"
                        >
                          <div className="flex items-start gap-3">
                            {orderItem?.product?.primaryImage && (
                              <img
                                src={orderItem.product.primaryImage}
                                alt={orderItem.product.name}
                                className="w-16 h-16 object-contain border border-gray-200 rounded-md"
                              />
                            )}

                            <div className="flex-grow">
                              <h4 className="font-medium text-gray-800">
                                {orderItem?.product?.name || 'Produkt nieznany'}
                              </h4>

                              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                <div>
                                  <span className="text-gray-500">
                                    Ilość zwracana:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {item.quantity}
                                  </span>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    Kod produktu:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {orderItem?.product?.serialNumber || 'N/A'}
                                  </span>
                                </div>

                                <div className="col-span-2">
                                  <span className="text-gray-500">
                                    Powód zwrotu:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {translateReturnReason(
                                      item.reason as ReturnReason,
                                    )}
                                  </span>
                                </div>

                                {item.description && (
                                  <div className="col-span-2 bg-gray-50 p-2 rounded-md mt-1">
                                    <span className="text-gray-500">Opis:</span>{' '}
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500">Brak przedmiotów do zwrotu</p>
                  )}
                </div>
              </div>

              <div className="p-5">
                <h3 className="text-base font-medium text-gray-800 mb-3">
                  Dane kontaktowe
                </h3>
                <div className="space-y-1 text-gray-600">
                  {returnData.order?.fullName && (
                    <p>
                      <span className="text-gray-500">Imię i nazwisko:</span>{' '}
                      {returnData.order.fullName}
                    </p>
                  )}
                  {returnData.order?.email && (
                    <p>
                      <span className="text-gray-500">Email:</span>{' '}
                      {returnData.order.email}
                    </p>
                  )}
                  {returnData.order?.phone && (
                    <p>
                      <span className="text-gray-500">Telefon:</span>{' '}
                      {returnData.order.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Prawa kolumna - podsumowanie zwrotu */}
          <motion.div variants={itemVariants}>
            <div>
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
                <div className="p-5 border-b border-gray-100">
                  <h3 className="text-base font-medium text-gray-800 mb-3">
                    Podsumowanie zwrotu
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Status zwrotu:</span>
                      <span
                        className={`font-medium ${
                          returnData.status === ReturnStatus.APPROVED
                            ? 'text-blue-600'
                            : returnData.status === ReturnStatus.COMPLETED
                              ? 'text-green-600'
                              : returnData.status === ReturnStatus.REJECTED
                                ? 'text-red-600'
                                : 'text-amber-600'
                        }`}
                      >
                        {translateReturnStatus(returnData.status)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Numer zamówienia:</span>
                      <Link
                        to={`/zamówienie/${returnData.orderId}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {returnData.order?.orderNumber || 'N/A'}
                      </Link>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Status zamówienia:</span>
                      <span className="font-medium">
                        {returnData.order?.orderStatus
                          ? translateOrderStatus(returnData.order.orderStatus)
                          : 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        Data złożenia zwrotu:
                      </span>
                      <span className="font-medium">
                        {new Date(returnData.createdAt).toLocaleDateString(
                          'pl-PL',
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="text-base font-medium text-gray-800 mb-3">
                    Szczegóły finansowe
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        Liczba zwracanych produktów:
                      </span>
                      <span className="font-medium">
                        {returnData.returnItems?.reduce(
                          (acc, item) => acc + item.quantity,
                          0,
                        ) || 0}
                      </span>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                      <span className="font-medium text-gray-700">
                        Kwota do zwrotu:
                      </span>
                      <span className="text-lg font-bold text-primary">
                        {calculateTotalRefund()} zł
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                <h3 className="text-base font-medium text-gray-800 mb-3">
                  Instrukcje i informacje
                </h3>

                <div className="space-y-4 text-sm text-gray-600">
                  {returnData.status === ReturnStatus.PENDING && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="font-medium text-amber-800 mb-1">
                        Oczekiwanie na weryfikację
                      </p>
                      <p>
                        Twój zwrot oczekuje na weryfikację przez nasz zespół.
                        Proces ten może potrwać do 3 dni roboczych.
                      </p>
                    </div>
                  )}

                  {returnData.status === ReturnStatus.APPROVED && (
                    <div
                      className={`bg-${config.theme.mainColor}-50 border border-${config.theme.mainColor}-200 rounded-md p-3`}
                    >
                      <p
                        className={`font-medium text-${config.theme.mainColor}-800 mb-1`}
                      >
                        Zwrot zatwierdzony
                      </p>
                      <p>
                        Twój zwrot został zatwierdzony. Prosimy o odesłanie
                        produktów na adres:
                      </p>
                      <p className="font-medium mt-2">
                        {config.company.name}
                        <br />
                        {config.contact.address.street}
                        <br />
                        {config.contact.address.postal}{' '}
                        {config.contact.address.city}
                      </p>
                      <p className="mt-2">
                        Do paczki prosimy dołączyć numer zwrotu:{' '}
                        <span className="font-medium">
                          {returnData.returnNumber}
                        </span>{' '}
                        w celu przyspieszenia procesu.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={generateReturnPdf}
                          disabled={isPdfGenerating}
                          className={`flex items-center gap-2 px-4 py-2 bg-${config.theme.mainColor}-600 text-white rounded-md hover:bg-${config.theme.mainColor}-700 transition-colors ${isPdfGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Download fontSize="small" />
                          {isPdfGenerating
                            ? 'Generowanie...'
                            : 'Pobierz dokument zwrotu'}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          Możesz wydrukować ten dokument i dołączyć go do paczki
                          zwrotnej. Nie jest to wymagane, ale pomoże nam
                          szybciej zidentyfikować i przetworzyć Twój zwrot.
                        </p>
                      </div>
                    </div>
                  )}

                  {returnData.status === ReturnStatus.COMPLETED && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <p className="font-medium text-green-800 mb-1">
                        Zwrot zakończony
                      </p>
                      <p>
                        Twój zwrot został zakończony. Dziękujemy za skorzystanie
                        z naszych usług!
                      </p>
                    </div>
                  )}

                  {returnData.status === ReturnStatus.REJECTED && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="font-medium text-red-800 mb-1">
                        Zwrot odrzucony
                      </p>
                      <p>
                        Niestety Twój zwrot został odrzucony. W celu wyjaśnienia
                        sytuacji skontaktuj się z naszym działem obsługi
                        klienta.
                      </p>
                    </div>
                  )}

                  <div className="mt-4">
                    <p className="font-medium mb-1">
                      Kontakt do działu obsługi klienta:
                    </p>
                    <p>
                      Email:{' '}
                      <a
                        href={`mailto:${config.contact.email}`}
                        className="text-primary hover:underline"
                      >
                        ${config.contact.email}
                      </a>
                    </p>
                    <p>
                      Telefon:{' '}
                      <a
                        href="tel:+48604856094"
                        className="text-primary hover:underline"
                      >
                        +48 604 856 094
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Modals */}
        <Confirm
          message="Czy na pewno chcesz zatwierdzić ten zwrot? Klient zostanie poinformowany o konieczności odesłania produktów."
          onConfirm={handleApprove}
          isOpen={showApproveModal}
          onClose={() => setShowApproveModal(false)}
        />

        <Confirm
          message="Czy na pewno chcesz odrzucić ten zwrot? Klient zostanie poinformowany o decyzji."
          onConfirm={handleReject}
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
        />

        <Confirm
          message="Czy na pewno chcesz oznaczyć ten zwrot jako zakończony? Ta operacja jest nieodwracalna."
          onConfirm={handleComplete}
          isOpen={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
        />

        <Confirm
          message="Czy na pewno chcesz przetworzyć zwrot pieniędzy dla klienta? Ta operacja jest nieodwracalna."
          onConfirm={handleRefund}
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
        />
      </motion.div>
      <Seo
        title={`Zwrot ${returnData.returnNumber}`}
      ></Seo>
    </>
  );
}

export default Return;
